import { Shield } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

/**
 * PrivacyBlock - Bloque de primera capa RGPD reutilizable para formularios publicos.
 *
 * Props:
 *  - privacyText: string - Texto legal de primera capa (Responsable, Finalidad, Derechos)
 *  - policyUrl: string - URL de la politica de privacidad
 *  - contactEmail: string - Email RGPD
 *  - checkboxText: string - Texto del checkbox obligatorio
 *  - accepted: boolean - Estado del checkbox obligatorio
 *  - onAcceptedChange: (val) => void
 *  - showCommercial: boolean - Mostrar checkbox de comunicaciones comerciales
 *  - commercialText: string
 *  - commercialAccepted: boolean
 *  - onCommercialChange: (val) => void
 *  - error: string|null - Mensaje de error de validacion
 */
export function PrivacyBlock({
  privacyText,
  policyUrl = "/politica-de-privacidad",
  contactEmail,
  checkboxText = "He leido y acepto la Politica de privacidad",
  accepted,
  onAcceptedChange,
  showCommercial = false,
  commercialText = "",
  commercialAccepted = false,
  onCommercialChange = () => {},
  error,
}) {
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4 mt-4" data-testid="privacy-block">
      {/* First layer info */}
      <div className="flex items-start gap-2.5">
        <Shield className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="privacy-block-text">
          {privacyText || "Informacion basica sobre proteccion de datos."}
          {policyUrl && (
            <>
              {" "}
              <Link
                to={policyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 underline hover:text-red-700 font-medium"
                data-testid="privacy-block-policy-link"
              >
                Politica de privacidad
              </Link>
              .
            </>
          )}
          {contactEmail && (
            <>
              {" "}Derechos RGPD:{" "}
              <a
                href={`mailto:${contactEmail}`}
                className="text-slate-600 underline hover:text-slate-800"
              >
                {contactEmail}
              </a>
              .
            </>
          )}
        </p>
      </div>

      {/* Mandatory checkbox */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="privacy-accept"
          checked={accepted}
          onCheckedChange={onAcceptedChange}
          className="mt-0.5"
          data-testid="privacy-block-checkbox"
        />
        <label
          htmlFor="privacy-accept"
          className="text-xs text-slate-700 leading-relaxed cursor-pointer select-none"
        >
          {checkboxText}{" "}
          {policyUrl && (
            <Link
              to={policyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-600 underline hover:text-red-700"
            >
              Politica de privacidad
            </Link>
          )}
        </label>
      </div>
      {error && (
        <p className="text-xs text-red-600 ml-7" data-testid="privacy-block-error">
          {error}
        </p>
      )}

      {/* Optional commercial checkbox */}
      {showCommercial && (
        <div className="flex items-start gap-3">
          <Checkbox
            id="privacy-commercial"
            checked={commercialAccepted}
            onCheckedChange={onCommercialChange}
            className="mt-0.5"
            data-testid="privacy-block-commercial-checkbox"
          />
          <label
            htmlFor="privacy-commercial"
            className="text-[11px] text-slate-500 leading-relaxed cursor-pointer select-none"
          >
            {commercialText || "Acepto recibir comunicaciones comerciales."}
          </label>
        </div>
      )}
    </div>
  );
}
